/**
 * JWT Authentication Service for Web Frontend
 * Handles JWT token management, validation, and refresh
 */

import { API_BASE_URL } from './apiConfig.js';

class JWTAuthService {
    constructor() {
        this.refreshTokenPromise = null;
        // Use the shared API URL from apiConfig
        this.API_BASE_URL = API_BASE_URL;
    }

    // Token Management
    getTokens() {
        const accessToken = sessionStorage.getItem('wiziai_access_token');
        const refreshToken = sessionStorage.getItem('wiziai_refresh_token');
        const expiresIn = sessionStorage.getItem('wiziai_expires_in');
        const refreshExpiresIn = sessionStorage.getItem('wiziai_refresh_expires_in');

        if (accessToken && refreshToken) {
            return {
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_in: expiresIn ? parseInt(expiresIn) : 3600,
                refresh_expires_in: refreshExpiresIn ? parseInt(refreshExpiresIn) : 604800
            };
        }
        return null;
    }

    setTokens(tokens) {
        const now = Date.now();
        const accessTokenExpiry = now + (tokens.expires_in * 1000);
        const refreshTokenExpiry = now + (tokens.refresh_expires_in * 1000);

        sessionStorage.setItem('wiziai_access_token', tokens.access_token);
        sessionStorage.setItem('wiziai_refresh_token', tokens.refresh_token);
        sessionStorage.setItem('wiziai_expires_in', tokens.expires_in.toString());
        sessionStorage.setItem('wiziai_refresh_expires_in', tokens.refresh_expires_in.toString());
        sessionStorage.setItem('wiziai_access_token_expiry', accessTokenExpiry.toString());
        sessionStorage.setItem('wiziai_refresh_token_expiry', refreshTokenExpiry.toString());
    }

    clearTokens() {
        sessionStorage.removeItem('wiziai_access_token');
        sessionStorage.removeItem('wiziai_refresh_token');
        sessionStorage.removeItem('wiziai_expires_in');
        sessionStorage.removeItem('wiziai_refresh_expires_in');
        sessionStorage.removeItem('wiziai_access_token_expiry');
        sessionStorage.removeItem('wiziai_refresh_token_expiry');
        sessionStorage.removeItem('wiziai_username');
        sessionStorage.removeItem('wiziai_display_name');
        sessionStorage.removeItem('wiziai_user_id');
        sessionStorage.removeItem('wiziai_email');
        sessionStorage.removeItem('wiziai_school');
        sessionStorage.removeItem('wiziai_role_id');
        sessionStorage.removeItem('wiziai_must_change_password');
    }

    isTokenExpired(token, expiry) {
        if (!expiry) return true;
        const expiryTime = parseInt(expiry);
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
        return now >= (expiryTime - bufferTime);
    }

    async getValidAccessToken() {
        const tokens = this.getTokens();
        if (!tokens) return null;

        const accessTokenExpiry = sessionStorage.getItem('wiziai_access_token_expiry');
        
        // Check if access token is still valid
        if (!this.isTokenExpired(tokens.access_token, accessTokenExpiry || '')) {
            return tokens.access_token;
        }

        // Try to refresh the token
        return this.refreshAccessToken();
    }

    async refreshAccessToken() {
        // Prevent multiple simultaneous refresh requests
        if (this.refreshTokenPromise) {
            return this.refreshTokenPromise;
        }

        const tokens = this.getTokens();
        if (!tokens) return null;

        const refreshTokenExpiry = sessionStorage.getItem('wiziai_refresh_token_expiry');
        
        // Check if refresh token is expired
        if (this.isTokenExpired(tokens.refresh_token, refreshTokenExpiry || '')) {
            this.clearTokens();
            return null;
        }

        this.refreshTokenPromise = this.performTokenRefresh(tokens.refresh_token);
        
        try {
            const newAccessToken = await this.refreshTokenPromise;
            return newAccessToken;
        } finally {
            this.refreshTokenPromise = null;
        }
    }

    async performTokenRefresh(refreshToken) {
        try {
            const response = await fetch(`${this.API_BASE_URL}/login/jwt_refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refresh_token: refreshToken })
            });

            if (!response.ok) {
                throw new Error('Refresh token request failed');
            }

            const data = await response.json();
            
            if (data.status === 'success' && data.result) {
                const newTokens = {
                    access_token: data.result.access_token,
                    refresh_token: data.result.refresh_token,
                    expires_in: data.result.expires_in,
                    refresh_expires_in: data.result.refresh_expires_in
                };
                
                this.setTokens(newTokens);
                return data.result.access_token;
            } else {
                this.clearTokens();
                return null;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.clearTokens();
            return null;
        }
    }

    // Authentication Methods
    async login(credentials) {
        try {
            const response = await fetch(`${this.API_BASE_URL}/login/jwt_login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...credentials,
                    device_info: 'Web App'
                })
            });

            if (!response.ok) {
                throw new Error('Login request failed');
            }

            const data = await response.json();
            
            if (data.status === 'success' && data.result) {
                const { user, access_token, refresh_token, expires_in, refresh_expires_in } = data.result;
                
                // Store JWT tokens
                this.setTokens({
                    access_token,
                    refresh_token,
                    expires_in,
                    refresh_expires_in
                });
                
                // Store user information
                sessionStorage.setItem('wiziai_username', user.username);
                sessionStorage.setItem('wiziai_display_name', user.display_name || user.username);
                sessionStorage.setItem('wiziai_user_id', user.id?.toString() || '');
                sessionStorage.setItem('wiziai_email', user.email || '');
                
                // Store mobile verification status from response (at result level, not user level).
                // The server says when it is not required -- a school student, whom the school
                // enrolled -- and then the OTP screen must not stand in their way.
                const mobileVerified = data.result.mobile_verified === true || data.result.mobile_verified === 1 || data.result.mobile_verified === '1'
                    || data.result.requires_mobile_verification === false;
                sessionStorage.setItem('wiziai_mobile_verified', mobileVerified.toString());

                // A school session: which school (display only -- the token decides where
                // requests go), and whether the temporary password must be replaced first.
                if (data.result.school) {
                    sessionStorage.setItem('wiziai_school', JSON.stringify(data.result.school));
                    sessionStorage.setItem('wiziai_role_id', String(data.result.role_id || ''));
                } else {
                    sessionStorage.removeItem('wiziai_school');
                    sessionStorage.removeItem('wiziai_role_id');
                }
                if (data.result.must_change_password) {
                    sessionStorage.setItem('wiziai_must_change_password', 'true');
                } else {
                    sessionStorage.removeItem('wiziai_must_change_password');
                }
                
                return {
                    success: true,
                    user: user,
                    message: data.message
                };
            } else {
                return {
                    success: false,
                    message: data.message || 'Login failed'
                };
            }
        } catch (error) {
            console.error('JWT Login Error:', error);
            return {
                success: false,
                message: error.message || 'Network error during login'
            };
        }
    }

    async logout() {
        try {
            const accessToken = await this.getValidAccessToken();
            if (accessToken) {
                // Call logout API to revoke session
                await fetch(`${this.API_BASE_URL}/login/jwt_logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({})
                });
            }
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            // Always clear local tokens
            this.clearTokens();
        }
        return { success: true };
    }

    // Check if user is authenticated
    async isAuthenticated() {
        try {
            const accessToken = await this.getValidAccessToken();
            return !!accessToken;
        } catch (error) {
            console.error('Auth check failed:', error);
            return false;
        }
    }

    // Get current user information
    getCurrentUser() {
        const username = sessionStorage.getItem('wiziai_username');
        const displayName = sessionStorage.getItem('wiziai_display_name');
        const userId = sessionStorage.getItem('wiziai_user_id');
        const email = sessionStorage.getItem('wiziai_email');
        
        if (username) {
            return {
                id: userId,
                username: username,
                display_name: displayName || username,
                email: email
            };
        }
        
        return null;
    }

    // Add authorization header to requests
    async getAuthHeaders() {
        const accessToken = await this.getValidAccessToken();
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }
        
        return headers;
    }
}

// Create and export a singleton instance
const jwtAuthService = new JWTAuthService();
export default jwtAuthService;
